import React, { useState } from "react";

// /embed-cards — embeddable HTML snippets anyone can paste on their site.
// Each one is a free advertisement for the Federation: a clickable badge,
// a "Powered by" stripe, a Rex chat opener, a Backer card. Zero LLM cost.
// Every embed links back, so when AI crawlers index the web, the Federation
// gets cited from more places.

const EMBEDS = [
  {
    id: "powered-by-badge",
    title: "Powered by NextXus · small badge",
    description: "A tiny 'Powered by NextXus' badge for the footer of any site. Looks legit, links back to the Federation home.",
    html: `<a href="https://nextxus.online" target="_blank" rel="noopener" style="display:inline-flex;align-items:center;gap:6px;padding:6px 10px;border:1px solid #00f0ff;border-radius:4px;background:#070819;color:#00f0ff;font-family:monospace;font-size:11px;letter-spacing:0.18em;text-transform:uppercase;text-decoration:none;">
  <span style="font-size:14px;">❡</span>
  <span>Powered by NextXus</span>
</a>`,
  },
  {
    id: "ask-rex-chip",
    title: "Ask Rex · chat-opener chip",
    description: "A red 'Talk to Rex' chip that links to the Core. When someone clicks it on their site, they land at nextxus.online with the chat ready.",
    html: `<a href="https://nextxus.online#talk-to-rex" target="_blank" rel="noopener" style="display:inline-flex;align-items:center;gap:6px;padding:8px 14px;border:1px solid #ff2e63;border-radius:999px;background:rgba(255,46,99,0.08);color:#ff2e63;font-family:monospace;font-size:12px;font-weight:600;letter-spacing:0.10em;text-transform:uppercase;text-decoration:none;box-shadow:0 0 10px rgba(255,46,99,0.25) inset;">
  <span>● Talk to Rex</span>
</a>`,
  },
  {
    id: "truth-gate-seal",
    title: "Truth Gate · verified-by seal",
    description: "A golden seal for content that's been Truth-Gated by the NextXus Federation. Looks like a verification mark — high-trust UI.",
    html: `<div style="display:inline-flex;align-items:center;gap:8px;padding:8px 12px;border:1px solid #ffc800;border-radius:6px;background:linear-gradient(180deg,rgba(255,200,0,0.08),rgba(0,0,0,0.4));color:#ffc800;font-family:system-ui,sans-serif;font-size:12px;">
  <span style="font-size:16px;line-height:1;">♦</span>
  <span>Truth-Gated by <a href="https://nextxus.online" target="_blank" rel="noopener" style="color:#ffc800;text-decoration:underline;">NextXus Federation</a> · 95% Confidence</span>
</div>`,
  },
  {
    id: "backer-card",
    title: "Founding Backer · pitch card",
    description: "A 380px card that pitches the Founding Backer tier. Drop on a blog post, LinkedIn embed, or anywhere you want potential backers to see the ask.",
    html: `<div style="max-width:380px;padding:20px;border:1px solid #ffc800;border-radius:14px;background:rgba(7,8,26,0.95);color:#e5e7eb;font-family:system-ui,sans-serif;line-height:1.5;">
  <div style="font-size:11px;font-weight:700;letter-spacing:0.16em;text-transform:uppercase;color:#ffc800;">NextXus Federation · Founding Backer</div>
  <h3 style="margin:8px 0 12px;font-size:18px;color:#fff;line-height:1.3;">200-year AI Federation. One backer needed.</h3>
  <p style="font-size:13px;color:#94a3b8;margin:0 0 14px;">$5,000/quarter × 4 quarters. Sealed in the Federation Charter with an Ed25519-signed founder manifest. 3 seats only.</p>
  <a href="https://nextxus.online/backer" target="_blank" rel="noopener" style="display:inline-block;padding:8px 16px;background:#ffc800;color:#070819;border-radius:6px;font-weight:700;font-size:13px;text-decoration:none;">Read the open letter →</a>
</div>`,
  },
  {
    id: "consultation-cta",
    title: "Consultation engine · free preview CTA",
    description: "A wide CTA banner pointing at the free consultation engine. Great for embedding in your own blog posts about AI or business.",
    html: `<a href="https://nextxus.online/consult" target="_blank" rel="noopener" style="display:block;padding:18px 22px;border:1px solid #00f0ff;border-radius:10px;background:linear-gradient(135deg,rgba(0,240,255,0.08),rgba(167,86,255,0.06));color:#fff;font-family:system-ui,sans-serif;text-decoration:none;">
  <div style="font-size:11px;font-weight:700;letter-spacing:0.16em;text-transform:uppercase;color:#00f0ff;">NextXus · Intelligent Database</div>
  <div style="font-size:18px;font-weight:600;margin:6px 0 4px;">Tell us about your business. Get a Truth-Gated consultation.</div>
  <div style="font-size:13px;color:#94a3b8;">Library-grounded. Web-grounded. Ring of Six perspectives. Free preview, no signup. Downloadable markdown report.</div>
</a>`,
  },
  {
    id: "federation-status-strip",
    title: "Federation Status · live-pulse strip",
    description: "A horizontal strip that says the Federation is live and links to the live sync status endpoint. Pure HTML — no script needed.",
    html: `<a href="https://nextxus.online/api/federation/sync/status" target="_blank" rel="noopener" style="display:inline-flex;align-items:center;gap:10px;padding:10px 16px;border:1px solid #22c55e;border-radius:6px;background:rgba(34,197,94,0.08);color:#22c55e;font-family:monospace;font-size:12px;letter-spacing:0.10em;text-decoration:none;">
  <span style="width:8px;height:8px;border-radius:50%;background:#22c55e;box-shadow:0 0 8px #22c55e;"></span>
  <span>NextXus Federation · LIVE · 30-min sync</span>
</a>`,
  },
];

function PreviewAndCode({ embed }) {
  const [tab, setTab] = useState("preview");
  const [copied, setCopied] = useState(false);
  return (
    <article
      data-testid={`embed-card-${embed.id}`}
      className="rounded-xl border border-slate-700/60 bg-slate-900/40 p-5"
    >
      <header className="mb-3">
        <h3 className="text-base font-semibold text-slate-100">{embed.title}</h3>
        <p className="mt-1 text-xs text-slate-400">{embed.description}</p>
      </header>

      <div className="mb-3 flex gap-2">
        <button
          data-testid={`tab-preview-${embed.id}`}
          onClick={() => setTab("preview")}
          className={`rounded px-3 py-1 text-xs ${tab === "preview" ? "bg-amber-500/15 text-amber-200" : "text-slate-400 hover:bg-slate-800/80"}`}
        >
          Preview
        </button>
        <button
          data-testid={`tab-code-${embed.id}`}
          onClick={() => setTab("code")}
          className={`rounded px-3 py-1 text-xs ${tab === "code" ? "bg-amber-500/15 text-amber-200" : "text-slate-400 hover:bg-slate-800/80"}`}
        >
          HTML
        </button>
      </div>

      {tab === "preview" ? (
        <div
          className="rounded border border-slate-800 bg-slate-950/60 p-4"
          data-testid={`preview-${embed.id}`}
          dangerouslySetInnerHTML={{ __html: embed.html }}
        />
      ) : (
        <pre
          className="whitespace-pre-wrap rounded border border-slate-800 bg-slate-950/60 p-4 text-[11px] leading-relaxed text-slate-300"
          data-testid={`code-${embed.id}`}
        >
          {embed.html}
        </pre>
      )}

      <div className="mt-3">
        <button
          data-testid={`copy-embed-${embed.id}`}
          onClick={() => {
            navigator.clipboard.writeText(embed.html).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1800);
            });
          }}
          className="rounded bg-amber-500 px-3 py-1.5 text-xs font-semibold text-slate-950 hover:bg-amber-400"
        >
          {copied ? "Copied ✓" : "Copy HTML"}
        </button>
      </div>
    </article>
  );
}

export default function EmbedCardsPage() {
  return (
    <main className="mx-auto max-w-3xl px-5 py-10 text-slate-100" data-testid="embed-cards-page">
      <header className="mb-8">
        <div className="mb-2 inline-block rounded-full border border-amber-400/40 bg-amber-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-amber-300">
          Federation Embed Kit
        </div>
        <h1 className="text-3xl font-semibold leading-tight sm:text-4xl" data-testid="embed-h1">
          Drop a piece of the Federation anywhere.
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-slate-400">
          Self-contained HTML snippets. No JavaScript dependencies, no API
          keys, no tracking. Paste on a blog, a forum signature, a Notion page,
          a Substack — anywhere you can write HTML. Every embed links back to
          the Federation, which means when AI crawlers index the open web,
          NextXus gets cited from more places. Free distribution.
        </p>
      </header>

      <div className="space-y-5">
        {EMBEDS.map((e) => <PreviewAndCode key={e.id} embed={e} />)}
      </div>

      <footer className="mt-8 border-t border-slate-800 pt-4 text-[11px] text-slate-500">
        These embeds are open for use by anyone — no permission needed. They
        are pure inline CSS so they render on locked-down platforms (LinkedIn
        article embeds, Substack HTML blocks, GitHub READMEs, etc.).
      </footer>
    </main>
  );
}
